import NotAvailable from "@/assets/not-available.svg";
import { ErrorPage } from "@/components/ErrorPage";
import { useUserAccess } from "@repo/api";
import { t } from "@repo/translation";
import { logError } from "@repo/utils";
import { type ReactNode, createContext, useEffect, useState } from "react";

import { type CoWorkerPreferences, useSyncCoworker } from "./useSyncCoworker";

type UserRole = "Admin" | "CoWorker";

interface Login {
  userRoles: UserRole[];
  coWorkerPreferences?: CoWorkerPreferences;
}

const LoginContext = createContext<Login>({
  userRoles: [],
  coWorkerPreferences: undefined,
});

interface Props {
  children: ReactNode;
}

export const LoginProvider = ({ children }: Props) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loadingDone, setLoadingDone] = useState(false);

  const coWorkerPreferences = useSyncCoworker();
  const { data: hasAccess, isLoading: accessLoading } = useUserAccess();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await window.xdk?.authentication?.getUser();
        setIsLoggedIn(Boolean(user));
      } catch (err) {
        logError(err);
      } finally {
        setLoadingDone(true);
      }
    };

    fetchUser();
  }, [hasAccess, accessLoading]);

  const isReady = loadingDone && !accessLoading;

  if (!isReady || !isLoggedIn) {
    return null;
  }

  if (isReady && isLoggedIn && !hasAccess) {
    return (
      <ErrorPage
        image={<NotAvailable />}
        title={t("prompt.no_access_to_this_application")}
        description={t("prompt.no_access_to_this_application_description")}
      />
    );
  }

  return (
    <LoginContext.Provider value={{ userRoles: [], coWorkerPreferences }}>
      {children}
    </LoginContext.Provider>
  );
};
